/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { getSnapshot } from 'mobx-state-tree';
/**
 * a set of APIs for compare card operations
 * @module CompareCardAPI
 */
export default class CompareCardAPI {
	constructor(compareModel) {

		/**
		 * To be invoked by the product only.
		 */
		this.destroy = function() {
			compareModel = null;
		};

		/**
		 * @return {object} Snapshot of the state
		 */
		this.getState = function() {
			return getSnapshot(compareModel);
		};

		/**
		 * toggle show compare line
		 */
		this.toggleShowCompareLine = function() {
			compareModel.toggleShowCompareLine();
		};

		/**
		 * verify if compare line enabled
		 * @return if compare line enabled
		 */
		this.isCompareLineEnabled = function() {
			return compareModel.isCompareLineEnabled();
		};

		/**
		 * verify if comapre line shown
		 * @return if compare line shown
		 */
		this.isCompareLineShown = function() {
			return compareModel.isCompareLineShown();
		};

		/**
		 * Modify whether compare lines are synced
		 * @param {boolean} sync - whether to sync compare lines
		 */
		this.setSyncCompareLine = function(sync) {
			compareModel.setCompareLineSynced(sync);
		};

		/**
		 * Check whether compare lines are synced
		 * @return {boolean} whether compare lines are synced
		 */
		this.isCompareLineSynced = function() {
			return compareModel.isCompareLineSynced();
		};

		/**
		 * Get points which match the current position of the crosshair
		 * @return {array} points matching crosshair
		 */
		this.getCrossHairMatchingPoints = function() {
			return compareModel.getCrossHairMatchingPoints();
		};

		/**
		 * Modify position of crosshair
		 * @param {string} widgetId - which widget's crosshair to move
		 * @param {number} value - new crosshair value
		 */
		this.onMoveCrosshairs = function(widgetId, value) {
			return compareModel.onMoveCrosshairs(compareModel.refChecker, widgetId, { value });
		};

		/**
		 * get the chart summary model owned by this compareModel.
		 * @return the summary model for summary stats
		 */
		this.getChartSummaryModel = function() {
			return compareModel.getChartSummaryModel('summary');
		};

		this.getCompareTableAPI = function(type='summary') {
			const compareTableSummary = compareModel.getChartSummaryModel(type);
			return compareTableSummary && compareTableSummary.getAPI();
		};

		/**
		 * Assemble a call to the stats processor for the summary stats
		 * to test the statsProcessor transforms on this compareModel and its contained statsModels.
		 * Example Return Value:
		 * [{
		 *		visId: 'viz1',
		 *		measures: [{
		 *			measureName: 'salesAndMarketing_csv.Revenue_countdistinct_data',
		 *			stats: { total: '24,009', totalSelection: '-', percentVsAllData: '52.7%', ... },
		 *			statsUse: { total: 24009, difference: 0, percentVsAllData: 52.7, summaryUse: 24009, ... },
		 *			toolTip: {
		 *				percentVsAllData: 'compareTable_tip_countdistinct_percentVsAllData',
		 *				percentChartVsChart: '',
		 *				total: 'compareTable_tip_countdistinct_total'
		 *			},
		 *			aggregationType": 'countdistinct',
		 *			meta: { itemId: 'salesAndMarketing_csv.Revenue', label: 'Revenue', format ... }
		 *		}]
		 *	},
		 *	{
		 *		visId: 'viz2',
		 *		measures: [{
		 *			measureName: 'salesAndMarketing_csv.Revenue_countdistinct_data', ...
		 *		},
		 *		{
		 *			measureName: 'salesAndMarketing_csv.Quantity_sum_data', ...
		 *		}]
		 *	}]
		 *
		 * @return An array by widgetid/measures in the compare model
		 * (each entry in widget measures array is the result of calling the statsProcessor getChartSummaryStats())
		 */
		this.getChartSummaryStats = function() {
			const results=[];
			compareModel.statsModelMap.keys().forEach((visId) => {
				const statsModelForVis = compareModel.statsModelMap.get(visId);
				const thisResult = { visId: visId, measures: [] };
				statsModelForVis.measures.keys().forEach((measureId) => {
					thisResult.measures.push(compareModel.theStatsProcessor.getChartSummaryStats(
						{
							newCompareTableStatsEnabled: compareModel.getNewCompareTableStatsEnabled(),
							measureName: measureId,
							statsModel: statsModelForVis,
							getString: (str) => str
						}
					));
				});
				results.push(thisResult);
			});
			return results;
		};
	}
}



// WEBPACK FOOTER //
// ./src/js/api/CompareCardAPI.js